import { useEffect, useMemo, useState } from "react";
import {
    Archive,
    Building2,
    ExternalLink,
    Globe2,
    Home,
    LockKeyhole,
    MapPin,
    Pencil,
    Plus,
    RefreshCw,
    Save,
    Search,
    X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getApiErrorMessage } from "@/lib/apiError";
import { isManagerOrAdmin, useAuth } from "@/lib/auth.jsx";

const PROPERTY_STATUSES = [
    "Available Now",
    "Coming Soon / In Progress",
    "Under Contract / Sold",
    "Status to Confirm",
];

const PROPERTY_TYPES = [
    "Finished Home",
    "Home in Progress",
    "Vacant Lot / Land",
    "Unknown",
];

const EMPTY_FORM = {
    street: "",
    city: "",
    state: "FL",
    zip: "",
    county: "",
    status: "Status to Confirm",
    property_type: "Unknown",
    units: "1",
    lot_size: "",
    land_price: "",
    package_price: "",
    sales_rep: "Unassigned",
    notes_internal: "",
    notes_public: "",
    source: "",
    public_visible: false,
    featured: false,
    compatible_home_ids_text: "",
    display_order: "0",
};

const STATUS_STYLES = {
    "Available Now": "border-emerald-200 bg-emerald-50 text-emerald-800",
    "Coming Soon / In Progress": "border-amber-200 bg-amber-50 text-amber-900",
    "Under Contract / Sold": "border-rose-200 bg-rose-50 text-rose-900",
    "Status to Confirm": "border-slate-200 bg-slate-100 text-slate-700",
};

function currency(value) {
    if (value === null || value === undefined || value === "") return "Not entered";
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "Not entered";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(amount);
}

function addressOf(property) {
    return [property.street, property.city, property.state, property.zip].filter(Boolean).join(", ");
}

function mapsUrl(property) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressOf(property))}`;
}

function toForm(property) {
    if (!property) return { ...EMPTY_FORM };
    return {
        street: property.street || "",
        city: property.city || "",
        state: property.state || "FL",
        zip: property.zip || "",
        county: property.county || "",
        status: property.status || "Status to Confirm",
        property_type: property.property_type || "Unknown",
        units: String(property.units ?? 1),
        lot_size: property.lot_size || "",
        land_price: property.land_price ?? "",
        package_price: property.package_price ?? "",
        sales_rep: property.sales_rep || "Unassigned",
        notes_internal: property.notes_internal || "",
        notes_public: property.notes_public || "",
        source: property.source || "",
        public_visible: Boolean(property.public_visible),
        featured: Boolean(property.featured),
        compatible_home_ids_text: (property.compatible_home_ids || []).join(", "),
        display_order: String(property.display_order ?? 0),
    };
}

function optionalMoney(value) {
    if (value === "" || value === null || value === undefined) return null;
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) return null;
    return amount;
}

function toPayload(form) {
    return {
        street: form.street.trim(),
        city: form.city.trim(),
        state: (form.state || "FL").trim().toUpperCase(),
        zip: form.zip.trim(),
        county: form.county.trim(),
        status: form.status,
        property_type: form.property_type,
        units: Math.max(1, Number.parseInt(form.units, 10) || 1),
        lot_size: form.lot_size.trim(),
        land_price: optionalMoney(form.land_price),
        package_price: optionalMoney(form.package_price),
        sales_rep: form.sales_rep.trim() || "Unassigned",
        notes_internal: form.notes_internal.trim(),
        notes_public: form.notes_public.trim(),
        source: form.source.trim(),
        public_visible: Boolean(form.public_visible),
        featured: Boolean(form.featured),
        compatible_home_ids: form.compatible_home_ids_text
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
        display_order: Math.max(0, Number.parseInt(form.display_order, 10) || 0),
    };
}

function StatCard({ label, value, detail, icon: Icon }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-medium text-slate-500">{label}</p>
                    <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
                    {detail && <p className="mt-1 text-xs text-slate-500">{detail}</p>}
                </div>
                <div className="rounded-xl bg-sky-50 p-2.5 text-[#0F2A47]">
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, hint, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
            {children}
            {hint && <span className="mt-1.5 block text-xs leading-5 text-slate-500">{hint}</span>}
        </label>
    );
}

function PropertyEditor({ property, onClose, onSaved }) {
    const isEditing = Boolean(property?.id);
    const [form, setForm] = useState(() => toForm(property));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(toForm(property));
    }, [property]);

    const setValue = (name, value) => {
        setForm((current) => ({ ...current, [name]: value }));
    };

    const save = async (event) => {
        event.preventDefault();
        const payload = toPayload(form);
        if (!payload.street) {
            toast.error("Street address is required");
            return;
        }

        setSaving(true);
        try {
            const response = isEditing
                ? await api.patch(`/properties/${property.id}`, payload)
                : await api.post("/properties", payload);
            toast.success(isEditing ? "Property updated" : "Property added");
            onSaved(response.data);
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Unable to save property"));
        } finally {
            setSaving(false);
        }
    };

    const inputClass = "h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-[#43a1d7] focus:ring-2 focus:ring-sky-100";
    const textareaClass = "min-h-28 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#43a1d7] focus:ring-2 focus:ring-sky-100";

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/55 p-3 py-8 sm:p-6" role="dialog" aria-modal="true" aria-label={isEditing ? "Edit property" : "Add property"}>
            <form onSubmit={save} className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-7">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1688c9]">Property package inventory</p>
                        <h2 className="mt-1 text-2xl font-semibold text-slate-900">{isEditing ? `Edit ${property.id}` : "Add a property"}</h2>
                        <p className="mt-1 text-sm text-slate-500">Internal fields stay in the quote portal. Only approved public fields can be published later.</p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close editor">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="grid gap-7 px-5 py-6 sm:px-7 lg:grid-cols-2">
                    <section>
                        <h3 className="text-base font-semibold text-slate-900">Property details</h3>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            <Field label="Street address" className="sm:col-span-2">
                                <input className={inputClass} value={form.street} onChange={(event) => setValue("street", event.target.value)} required />
                            </Field>
                            <Field label="City">
                                <input className={inputClass} value={form.city} onChange={(event) => setValue("city", event.target.value)} />
                            </Field>
                            <div className="grid grid-cols-[90px_1fr] gap-3">
                                <Field label="State">
                                    <input className={inputClass} maxLength={2} value={form.state} onChange={(event) => setValue("state", event.target.value)} />
                                </Field>
                                <Field label="ZIP">
                                    <input className={inputClass} value={form.zip} onChange={(event) => setValue("zip", event.target.value)} />
                                </Field>
                            </div>
                            <Field label="County">
                                <input className={inputClass} value={form.county} onChange={(event) => setValue("county", event.target.value)} />
                            </Field>
                            <Field label="Lot size / description">
                                <input className={inputClass} value={form.lot_size} onChange={(event) => setValue("lot_size", event.target.value)} placeholder="Example: 0.50 acre" />
                            </Field>
                            <Field label="Status">
                                <select className={inputClass} value={form.status} onChange={(event) => setValue("status", event.target.value)}>
                                    {PROPERTY_STATUSES.map((option) => <option key={option}>{option}</option>)}
                                </select>
                            </Field>
                            <Field label="Property type">
                                <select className={inputClass} value={form.property_type} onChange={(event) => setValue("property_type", event.target.value)}>
                                    {PROPERTY_TYPES.map((option) => <option key={option}>{option}</option>)}
                                </select>
                            </Field>
                            <Field label="Homes / sites">
                                <input className={inputClass} type="number" min="1" value={form.units} onChange={(event) => setValue("units", event.target.value)} />
                            </Field>
                            <Field label="Display order">
                                <input className={inputClass} type="number" min="0" value={form.display_order} onChange={(event) => setValue("display_order", event.target.value)} />
                            </Field>
                        </div>

                        <h3 className="mt-7 text-base font-semibold text-slate-900">Pricing and assignment</h3>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            <Field label="Land price">
                                <input className={inputClass} type="number" min="0" step="0.01" value={form.land_price} onChange={(event) => setValue("land_price", event.target.value)} placeholder="Optional" />
                            </Field>
                            <Field label="Estimated package price">
                                <input className={inputClass} type="number" min="0" step="0.01" value={form.package_price} onChange={(event) => setValue("package_price", event.target.value)} placeholder="Optional" />
                            </Field>
                            <Field label="Sales representative">
                                <input className={inputClass} value={form.sales_rep} onChange={(event) => setValue("sales_rep", event.target.value)} />
                            </Field>
                            <Field label="Source">
                                <input className={inputClass} value={form.source} onChange={(event) => setValue("source", event.target.value)} placeholder="Team message, MLS, owner, etc." />
                            </Field>
                            <Field label="Compatible home IDs" className="sm:col-span-2" hint="Comma-separated home IDs. This prepares the record for home matching in the quote builder.">
                                <input className={inputClass} value={form.compatible_home_ids_text} onChange={(event) => setValue("compatible_home_ids_text", event.target.value)} placeholder="home-id-1, home-id-2" />
                            </Field>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-base font-semibold text-slate-900">Internal and public information</h3>
                        <div className="mt-4 space-y-4">
                            <Field label="Internal notes" hint="Visible only to authenticated portal users. Never sent through the public property endpoint.">
                                <textarea className={textareaClass} value={form.notes_internal} onChange={(event) => setValue("notes_internal", event.target.value)} />
                            </Field>
                            <Field label="Public description" hint="Customer-safe language intended for the Land & Home Packages page.">
                                <textarea className={textareaClass} value={form.notes_public} onChange={(event) => setValue("notes_public", event.target.value)} />
                            </Field>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                <label className="flex cursor-pointer items-start gap-3">
                                    <input type="checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1688c9]" checked={form.public_visible} onChange={(event) => setValue("public_visible", event.target.checked)} />
                                    <span>
                                        <span className="block text-sm font-semibold text-slate-900">Approved for public website</span>
                                        <span className="mt-1 block text-xs leading-5 text-slate-500">Only safe public fields are exposed. Internal notes, source, land cost, and sales assignment remain private.</span>
                                    </span>
                                </label>
                                <label className="mt-4 flex cursor-pointer items-start gap-3 border-t border-slate-200 pt-4">
                                    <input type="checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1688c9]" checked={form.featured} onChange={(event) => setValue("featured", event.target.checked)} />
                                    <span>
                                        <span className="block text-sm font-semibold text-slate-900">Featured opportunity</span>
                                        <span className="mt-1 block text-xs leading-5 text-slate-500">Featured records sort ahead of standard opportunities.</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </section>
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" disabled={saving} className="bg-[#0F2A47] text-white hover:bg-[#14385E]">
                        {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        {isEditing ? "Save Changes" : "Add Property"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

export default function PropertyPackages() {
    const { user } = useAuth();
    const canManage = isManagerOrAdmin(user);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [typeFilter, setTypeFilter] = useState("All");
    const [visibilityFilter, setVisibilityFilter] = useState("All");
    const [sort, setSort] = useState("featured");
    const [editorProperty, setEditorProperty] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [archivingId, setArchivingId] = useState(null);

    const loadProperties = async () => {
        setLoading(true);
        try {
            const response = await api.get("/properties");
            setProperties(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Unable to load property inventory"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProperties();
    }, []);

    const metrics = useMemo(() => {
        const available = properties.filter((property) => property.status === "Available Now");
        return {
            records: properties.length,
            available: available.length,
            sites: available.reduce((sum, property) => sum + Number(property.units || 0), 0),
            public: properties.filter((property) => property.public_visible).length,
            confirm: properties.filter((property) => property.status === "Status to Confirm").length,
        };
    }, [properties]);

    const filtered = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        const result = properties.filter((property) => {
            const searchable = `${property.id} ${addressOf(property)} ${property.county || ""} ${property.sales_rep || ""} ${property.notes_internal || ""}`.toLowerCase();
            const visibilityMatches =
                visibilityFilter === "All" ||
                (visibilityFilter === "Public" && property.public_visible) ||
                (visibilityFilter === "Internal" && !property.public_visible);
            return (
                (!normalized || searchable.includes(normalized)) &&
                (statusFilter === "All" || property.status === statusFilter) &&
                (typeFilter === "All" || property.property_type === typeFilter) &&
                visibilityMatches
            );
        });

        return [...result].sort((a, b) => {
            if (sort === "city") return `${a.city || ""}${a.street || ""}`.localeCompare(`${b.city || ""}${b.street || ""}`);
            if (sort === "status") return `${a.status || ""}${a.display_order || 0}`.localeCompare(`${b.status || ""}${b.display_order || 0}`);
            if (sort === "sites") return Number(b.units || 0) - Number(a.units || 0);
            if (Boolean(a.featured) !== Boolean(b.featured)) return a.featured ? -1 : 1;
            return Number(a.display_order || 0) - Number(b.display_order || 0);
        });
    }, [properties, query, sort, statusFilter, typeFilter, visibilityFilter]);

    const openNew = () => {
        setEditorProperty(null);
        setEditorOpen(true);
    };

    const openEdit = (property) => {
        setEditorProperty(property);
        setEditorOpen(true);
    };

    const handleSaved = (saved) => {
        setProperties((current) => {
            const exists = current.some((property) => property.id === saved.id);
            return exists
                ? current.map((property) => property.id === saved.id ? saved : property)
                : [saved, ...current];
        });
        setEditorOpen(false);
        setEditorProperty(null);
    };

    const archiveProperty = async (property) => {
        if (!window.confirm(`Archive ${property.id} — ${addressOf(property)}? It will also be removed from public visibility.`)) return;
        setArchivingId(property.id);
        try {
            await api.delete(`/properties/${property.id}`);
            setProperties((current) => current.filter((item) => item.id !== property.id));
            toast.success("Property archived");
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Unable to archive property"));
        } finally {
            setArchivingId(null);
        }
    };

    const clearFilters = () => {
        setQuery("");
        setStatusFilter("All");
        setTypeFilter("All");
        setVisibilityFilter("All");
    };

    const inputClass = "h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-[#43a1d7] focus:ring-2 focus:ring-sky-100";

    return (
        <div className="mx-auto max-w-[1500px] space-y-6" data-testid="property-packages-page">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1688c9]">Land and package operations</p>
                    <h1 className="mt-1 text-3xl font-semibold text-slate-900">Property Package Manager</h1>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                        Maintain land, completed homes, in-progress properties, pricing, compatible home models, public visibility, and internal sales details from the authenticated quote portal.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={loadProperties} disabled={loading}>
                        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    {canManage && (
                        <Button onClick={openNew} className="bg-[#0F2A47] text-white hover:bg-[#14385E]" data-testid="add-property-button">
                            <Plus className="mr-2 h-4 w-4" /> Add Property
                        </Button>
                    )}
                </div>
            </div>

            {!canManage && (
                <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>You have view access. Property creation, publishing, pricing edits, and archiving require a manager or administrator.</p>
                </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <StatCard label="Active records" value={metrics.records} detail="Internal inventory" icon={Building2} />
                <StatCard label="Available now" value={metrics.available} detail="Property records" icon={MapPin} />
                <StatCard label="Available homes / sites" value={metrics.sites} detail="Across available records" icon={Home} />
                <StatCard label="Publicly approved" value={metrics.public} detail="Safe for website feed" icon={Globe2} />
                <StatCard label="Needs confirmation" value={metrics.confirm} detail="Status or details incomplete" icon={LockKeyhole} />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1.7fr)_1fr_1fr_1fr_1fr_auto]">
                    <label className="relative">
                        <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                        <input className={`${inputClass} w-full pl-9`} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search ID, address, county, rep, or notes" />
                    </label>
                    <select className={inputClass} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                        <option value="All">All statuses</option>
                        {PROPERTY_STATUSES.map((option) => <option key={option}>{option}</option>)}
                    </select>
                    <select className={inputClass} value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                        <option value="All">All property types</option>
                        {PROPERTY_TYPES.map((option) => <option key={option}>{option}</option>)}
                    </select>
                    <select className={inputClass} value={visibilityFilter} onChange={(event) => setVisibilityFilter(event.target.value)}>
                        <option value="All">All visibility</option>
                        <option value="Public">Publicly approved</option>
                        <option value="Internal">Internal only</option>
                    </select>
                    <select className={inputClass} value={sort} onChange={(event) => setSort(event.target.value)}>
                        <option value="featured">Featured / display order</option>
                        <option value="city">City / address</option>
                        <option value="status">Status</option>
                        <option value="sites">Most homes / sites</option>
                    </select>
                    <Button variant="outline" onClick={clearFilters}>Clear</Button>
                </div>
            </div>

            {loading ? (
                <div className="flex min-h-64 items-center justify-center rounded-2xl border border-slate-200 bg-white">
                    <div className="text-center text-slate-500">
                        <RefreshCw className="mx-auto h-6 w-6 animate-spin" />
                        <p className="mt-3 text-sm">Loading property inventory...</p>
                    </div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                    <MapPin className="mx-auto h-8 w-8 text-slate-400" />
                    <h2 className="mt-3 text-lg font-semibold text-slate-900">No properties match these filters</h2>
                    <p className="mt-1 text-sm text-slate-500">Clear the filters or add a new property record.</p>
                </div>
            ) : (
                <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                    {filtered.map((property) => (
                        <article key={property.id} className="flex min-h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-sky-200 hover:shadow-md">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-[#1688c9]">{property.id}</span>
                                        {property.featured && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">Featured</span>}
                                    </div>
                                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-900">{property.street}</h2>
                                    <p className="mt-1 text-sm text-slate-500">{[property.city, property.state, property.zip].filter(Boolean).join(", ") || "Location details incomplete"}</p>
                                </div>
                                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLES[property.status] || STATUS_STYLES["Status to Confirm"]}`}>{property.status}</span>
                            </div>

                            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-xl bg-slate-50 p-3">
                                    <p className="text-xs text-slate-500">Type</p>
                                    <p className="mt-1 font-medium text-slate-900">{property.property_type}</p>
                                </div>
                                <div className="rounded-xl bg-slate-50 p-3">
                                    <p className="text-xs text-slate-500">Homes / sites</p>
                                    <p className="mt-1 font-medium text-slate-900">{property.units || 1}</p>
                                </div>
                                <div className="rounded-xl bg-slate-50 p-3">
                                    <p className="text-xs text-slate-500">Land price</p>
                                    <p className="mt-1 font-medium text-slate-900">{currency(property.land_price)}</p>
                                </div>
                                <div className="rounded-xl bg-slate-50 p-3">
                                    <p className="text-xs text-slate-500">Package price</p>
                                    <p className="mt-1 font-medium text-slate-900">{currency(property.package_price)}</p>
                                </div>
                            </div>

                            <div className="mt-4 space-y-2 text-sm text-slate-600">
                                <p><span className="font-medium text-slate-800">County:</span> {property.county || "Not entered"}</p>
                                <p><span className="font-medium text-slate-800">Lot:</span> {property.lot_size || "Not entered"}</p>
                                <p><span className="font-medium text-slate-800">Sales rep:</span> {property.sales_rep || "Unassigned"}</p>
                                <p><span className="font-medium text-slate-800">Compatible homes:</span> {(property.compatible_home_ids || []).length || 0}</p>
                            </div>

                            {property.notes_internal && (
                                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Internal notes</p>
                                    <p className="mt-1 line-clamp-3 text-sm leading-6 text-slate-700">{property.notes_internal}</p>
                                </div>
                            )}

                            <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                                <a href={mapsUrl(property)} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
                                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Map
                                </a>
                                <span className={`inline-flex h-9 items-center rounded-md px-3 text-xs font-semibold ${property.public_visible ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                                    {property.public_visible ? <Globe2 className="mr-1.5 h-3.5 w-3.5" /> : <LockKeyhole className="mr-1.5 h-3.5 w-3.5" />}
                                    {property.public_visible ? "Website approved" : "Internal only"}
                                </span>
                                {canManage && (
                                    <div className="ml-auto flex gap-2">
                                        <Button variant="outline" size="sm" onClick={() => openEdit(property)}>
                                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => archiveProperty(property)} disabled={archivingId === property.id} className="text-rose-700 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800">
                                            {archivingId === property.id ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Archive className="mr-1.5 h-3.5 w-3.5" />}
                                            Archive
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            )}

            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 text-sm leading-6 text-slate-700">
                <p className="font-semibold text-[#0F2A47]">Architecture in place</p>
                <p className="mt-1">This manager now owns the operational property data in MongoDB. The public website can consume only the sanitized public endpoint. The next integration step is selecting a property inside the quote builder and adding land price to the package quote.</p>
            </div>

            {editorOpen && canManage && (
                <PropertyEditor
                    property={editorProperty}
                    onClose={() => { setEditorOpen(false); setEditorProperty(null); }}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}
